import { __assign, __read, __spread } from "tslib";
import { sum } from 'd3-array';
import { arc, pie } from 'd3-shape';
import { calculateHypotenuse, formatValue as valueFormatter, getOffsetRangeColor, } from '@keen.io/charts-utils';
import { colors as palette } from '@keen.io/colors';
import { OTHERS_DATA_KEY } from '../constants';
export var createStackedSlice = function (_a) {
    var slices = _a.slices, total = _a.total, treshold = _a.treshold, slicesToStack = _a.slicesToStack, colors = _a.colors, _b = _a.dataSeriesOffset, dataSeriesOffset = _b === void 0 ? [0, colors.length] : _b;
    var filteredSlices = slices;
    var stackValue = slicesToStack.reduce(function (total, element) { return total + element.value; }, 0);
    var stack = slicesToStack.map(function (_a) {
        var color = _a.color, selector = _a.selector;
        return ({
            color: color,
            selector: selector,
        });
    });
    filteredSlices = slices
        .filter(function (_a) {
        var value = _a.value;
        return (value * 100) / total > treshold;
    })
        .map(function (el, idx) { return (__assign(__assign({}, el), { color: getOffsetRangeColor(idx, colors, dataSeriesOffset) })); });
    var inOffsetRange = filteredSlices.length >= dataSeriesOffset[0] &&
        filteredSlices.length < dataSeriesOffset[1];
    filteredSlices.push({
        color: inOffsetRange
            ? palette.gray['500']
            : getOffsetRangeColor(filteredSlices.length, colors, dataSeriesOffset),
        dataKey: OTHERS_DATA_KEY,
        value: stackValue,
        selector: [],
        stacked: true,
        stack: stack,
    });
    return filteredSlices;
};
export var getSlicesToStack = function (_a) {
    var _b = _a.slices, slices = _b === void 0 ? [] : _b, total = _a.total, tresholdPercent = _a.tresholdPercent;
    return slices.filter(function (slice) { return (slice.value * 100) / total <= tresholdPercent; });
};
export var calculateTresholdPercent = function (total, treshold) {
    return (treshold * 100) / total;
};
export var generateCircularChart = function (_a) {
    var data = _a.data, colors = _a.colors, dimension = _a.dimension, padAngle = _a.padAngle, padRadius = _a.padRadius, cornerRadius = _a.cornerRadius, innerRadius = _a.innerRadius, labelSelector = _a.labelSelector, keys = _a.keys, disabledLabels = _a.disabledLabels, labelsPosition = _a.labelsPosition, labelsRadius = _a.labelsRadius, margins = _a.margins, _b = _a.type, type = _b === void 0 ? 'pie' : _b, treshold = _a.treshold, formatValue = _a.formatValue, dataSeriesOffset = _a.dataSeriesOffset;
    var slices = [];
    var width = dimension.width, height = dimension.height;
    var radius = Math.min(width - margins.left - margins.right, height - margins.top - margins.bottom) / 2;
    var relativeInnerRadius = radius * Math.min(innerRadius, 1);
    data.forEach(function (item, idx) {
        var label = item[labelSelector];
        var result = keys.reduce(function (acc, currentKey) {
            if (currentKey !== label)
                return acc + item[currentKey];
            return acc;
        }, 0);
        slices.push({
            value: result,
            dataKey: label,
            selector: [idx],
            color: getOffsetRangeColor(idx, colors, dataSeriesOffset),
        });
    });
    slices = slices
        .sort(function (a, b) { return b.value - a.value; })
        .map(function (slice, idx) { return (__assign(__assign({}, slice), { color: getOffsetRangeColor(idx, colors, dataSeriesOffset) })); });
    var total = sum(slices, function (d) { return d.value; });
    var createPie = pie()
        .sortValues(null)
        .value(function (d) { return d.value; });
    var createArc = arc().padAngle(padAngle);
    var tresholdPercent = calculateTresholdPercent(total, treshold);
    var slicesToStack = getSlicesToStack({ slices: slices, total: total, tresholdPercent: tresholdPercent });
    if (treshold > 0 && slicesToStack.length > 1) {
        slices = createStackedSlice({
            slices: slices,
            treshold: tresholdPercent,
            total: total,
            slicesToStack: slicesToStack,
            colors: colors,
            dataSeriesOffset: dataSeriesOffset,
        });
    }
    var stackedSlices = slicesToStack.map(function (slice) { return slice.dataKey; });
    var disabledSlices = __spread(disabledLabels, [
        disabledLabels.includes(stackedSlices[0]) && OTHERS_DATA_KEY,
    ]);
    slices = slices.filter(function (slice) { return !disabledSlices.includes(slice.dataKey); });
    var calculateLabelPosition = function (startAngle, endAngle) {
        var _a = __read(createArc.centroid({
            innerRadius: relativeInnerRadius,
            outerRadius: type === 'donut' ? radius : radius + labelsRadius,
            startAngle: startAngle,
            endAngle: endAngle,
        }), 2), x = _a[0], y = _a[1];
        if (labelsPosition === 'outside') {
            return calculateHypotenuse(x, y, radius + labelsRadius);
        }
        return [x, y];
    };
    var arcs = [];
    var sortedDataSeries = [];
    createPie(slices).forEach(function (_a, index) {
        var startAngle = _a.startAngle, endAngle = _a.endAngle, value = _a.value, sliceData = _a.data;
        var _b = sliceData, color = _b.color, selector = _b.selector, stacked = _b.stacked, stack = _b.stack, dataKey = _b.dataKey;
        var _c = __read(createArc.centroid({
            innerRadius: relativeInnerRadius,
            outerRadius: 0,
            startAngle: startAngle,
            endAngle: endAngle,
        }), 2), x = _c[0], y = _c[1];
        if (value > 0) {
            sortedDataSeries.push(dataKey);
            arcs.push({
                labelNumeric: valueFormatter(value, formatValue),
                labelPercentage: String((Math.round(value * 100) / total).toFixed(1) + "%"),
                labelPosition: calculateLabelPosition(startAngle, endAngle),
                activePosition: [x, y],
                index: String(index),
                dataKey: dataKey,
                selector: selector,
                color: color,
                startAngle: startAngle,
                endAngle: endAngle,
                stacked: stacked,
                stack: stack,
            });
        }
    });
    return {
        total: total,
        arcs: arcs,
        sortedDataSeries: sortedDataSeries,
        drawArc: arc()
            .padAngle(padAngle)
            .innerRadius(relativeInnerRadius)
            .outerRadius(radius)
            .padRadius(padRadius)
            .cornerRadius(cornerRadius),
    };
};
